import React, { useEffect, useRef, useState } from "react"
import { CheckIcon } from "@heroicons/react/24/outline"
import { StarIcon as StarSolid } from "@heroicons/react/24/solid"
import { StarIcon as StarOutline } from "@heroicons/react/24/outline"
import useClientAuth from "../../hooks/useClientAuth"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import { navigate } from "vike/client/router"
import Spinner from "../Spinner"
import Img from "../Img"
import useT from "../../hooks/useT"
import { useMoods } from "../../utils/moods"
import { SubmitReview } from "../../storeApi"
import clientRedirect from "../../utils/clientRedirect"
import { useSignInGate } from "../../utils/signIn"

const MAX_MOODS = 3

// sf_dialog, 600px wide on desktop. The review form: on a phone the close
// button has a row of its own and the title the whole width; on desktop they
// share the top row, with the product's picture beside the title
const REVIEW_DIALOG = {
  header: "relative flex flex-row items-center gap-4 px-4 md:pl-6 md:pr-14 pt-14 md:pt-6 pb-2 shrink-0",
  closeButton: "absolute right-3 top-3 md:top-4 z-20 p-2 rounded-full text-text_secondary hover:bg-neutral",
  closeIcon: "h-6 w-6",
  title: "flex-1 min-w-0 m-0 text-center md:text-left text-[20px] md:text-[22px] font-bold leading-tight",
  headerContent: "hidden md:flex order-first shrink-0",
  content: "w-full",
}

// the thank-you: no header title — the title goes with the content, under a
// checkmark, in the middle of the phone screen
const SUCCESS_DIALOG = {
  header: "relative h-14 shrink-0",
  closeButton: REVIEW_DIALOG.closeButton,
  closeIcon: REVIEW_DIALOG.closeIcon,
  title: "hidden",
  content: "w-full min-h-full flex flex-col justify-center pb-[15vh] md:pb-6",
}

// Star picker (1–5) with hover preview
function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0)
  const shown = hover || value || 0
  return (
    <div className="flex flex-row items-center justify-center gap-1" role="radiogroup" aria-label="rating" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          role="radio"
          aria-checked={value === i}
          aria-label={`${i}`}
          data-testid={`review-star-${i}`}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)}
          className="p-0.5 transition-transform hover:scale-110"
        >
          {i <= shown
            ? <StarSolid className="w-8 h-8 md:w-14 md:h-14 text-primary_hov" />
            : <StarOutline className="w-8 h-8 md:w-14 md:h-14 text-primary_hov" />}
        </button>
      ))}
    </div>
  )
}

/**
 * /…/leave-review — the "Rate this product" dialog of the old site: star
 * rating, (optional) moods, review text, submit → /…/leave-review/success.
 * `rootPath` is the product URL the dialog closes back to (#reviews).
 */
export default function LeaveReview({ product, rootPath, showMoods = false }) {
  const t = useT()
  const { auth } = useClientAuth()
  const moodsList = useMoods()
  const textRef = useRef(null)
  const [rating, setRating] = useState(0)
  const [moods, setMoods] = useState([])
  const [moodError, setMoodError] = useState(null)
  const [error, setError] = useState(null)
  const [pending, setPending] = useState(false)
  // server-rendered: the button stays disabled until React is attached
  const [ready, setReady] = useState(false)
  useEffect(() => setReady(true), [])

  // back to the product page as it was: no hash, so nothing scrolls
  const close = () => navigate(rootPath, { keepScrollPosition: true })

  // a guest signs in over the review form; closing it goes back to the product
  useSignInGate(auth, close)

  const toggleMood = (id) => {
    if (moods.includes(id)) {
      setMoods(moods.filter((m) => m !== id))
      setMoodError(null)
      return
    }
    if (moods.length >= MAX_MOODS) return setMoodError(t`You can select up to 3 moods`)
    setMoods([...moods, id])
    setMoodError(null)
  }

  const submit = async () => {
    if (!rating) return setError(t`Please select star rating`)
    setError(null)
    setPending(true)
    try {
      const result = await SubmitReview(product.pid, textRef.current?.value || "", rating, moods)
      if (result?.error) throw new Error(result.error)
      navigate(`${rootPath}/leave-review/success`)
    } catch (e) {
      setError(typeof e?.message === "string" ? t(e.message) : t`Something went wrong`)
    } finally {
      setPending(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={close}
      closeOnEscape
      closeOnClickOutside
      variant="sf_dialog"
      className="md:max-w-[600px]"
      styleOverrides={REVIEW_DIALOG}
      title={product?.name ?? ""}
      headerContent={product?.square && <Img src={product.square} alt="" width={56} height={56} className="w-[56px] h-[56px] rounded-md overflow-hidden border border-black/10" />}
    >
      <div className="flex flex-col px-6 pb-2" data-testid="leave-review-dialog">
        {/* on a phone the dialog is the whole screen: the product's picture
            says what is being rated */}
        {product?.square && (
          <div className="md:hidden -mx-6 my-4">
            <Img src={product.original || product.thumb || product.square} alt={product.name} width={600} height={740} fit="contain" className="w-full aspect-[180/222] overflow-hidden bg-neutral" sizes="100vw" />
          </div>
        )}
        <div className="flex flex-col items-center justify-center gap-1 mb-6">
          <Typography variant="txt_dim">
            {t`Rate this product`}
          </Typography>

          <StarPicker 
            value={rating} 
            onChange={(v) => { setRating(v); setError(null) }} 
          />
        </div>

        {/* {showMoods && (
          <div className="flex flex-col gap-2 mb-5">
            <span className="text-[16px] text-text_secondary text-center sm:text-left">{t`Select moods`}</span>
            <div className="flex flex-row flex-wrap gap-1.5">
              {moodsList.map((mood) => {
                const selected = moods.includes(mood.id)
                return (
                  <button
                    key={mood.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleMood(mood.id)}
                    className={`inline-flex items-center gap-1 h-[32px] px-3 rounded-full border text-[14px] transition-colors ${selected ? "border-primary text-primary font-semibold" : "border-bordered text-text"}`}
                  >
                    <span className="text-[18px] leading-none">{mood.emoji}</span>
                    {mood.displayText}
                  </button>
                )
              })}
            </div>
            {moodError && <span className="text-[14px] text-error">{moodError}</span>}
          </div>
        )} */}

        <textarea
          ref={textRef}
          name="review"
          rows={5}
          autoFocus
          required
          placeholder={t`Tell us what you think about this product.`}
          className="w-full rounded-md border border-bordered bg-paper p-3 text-[16px] outline-none focus:border-text resize-y"
        />
      </div>

      <div className="flex flex-col md:flex-row items-center justify-center md:justify-end gap-3 px-6 py-4">
        {error && <span className="text-[14px] text-error" data-testid="review-error">{error}</span>}
        <Button variant="primary" onClick={submit} disabled={pending || !ready} data-testid="review-submit">
          {pending ? <Spinner className="w-auto py-0" size={20} /> : t`Submit`}
        </Button>
      </div>
    </Modal>
  )
}

/** /…/leave-review/success — "Thank you! We've received your review." */
export function ReviewSuccessfullyAdded({ rootPath }) {
  const t = useT()
  // back to the product page as it was: no hash, so nothing scrolls
  const close = () => navigate(rootPath, { keepScrollPosition: true })
  return (
    <Modal 
      isOpen 
      onClose={close} 
      closeOnEscape 
      closeOnClickOutside 
      variant="sf_dialog" 
      className="md:max-w-[600px]" 
      styleOverrides={SUCCESS_DIALOG}
    >
      <div className="flex flex-col items-center gap-5 pb-6 px-6">
        <div 
          className="flex justify-center"   
          data-testid="review-success-dialog"
        >
          <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full border-2 border-text text-text">
            <CheckIcon className="h-10 w-10" strokeWidth={2.5} />
          </span>
        </div>

        <Typography 
          variant="h2" 
          className="text-center leading-tight"
        >
          {t`Thank you!`}
        </Typography>

        <Typography 
          variant="txt" 
          className="text-center"
        >
          {t`We've received your review.`}
        </Typography>

        <Button 
          variant="primary" 
          className="w-fit min-w-[160px]" 
          onClick={close} 
          data-testid="review-success-close"
        >
          {t`Close`}
        </Button>
        
      </div>
    </Modal>
  )
}
